/**
 * Date field — sélecteur de date, mois, plage de dates et heure.
 *
 * Les <input class="date-field__input"> de l'auteur restent les vrais champs du
 * formulaire (valeur ISO : YYYY-MM-DD, YYYY-MM, HH:MM) : ils sont masqués et
 * reçoivent les événements natifs input/change. Le JS insère devant eux un champ
 * d'affichage (saisie clavier) et un bouton qui ouvre un popover natif.
 */

// Accès différé : le module peut être importé côté serveur (rendu SSR) sans DOM
const VALUE = typeof HTMLInputElement !== 'undefined' ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value') : null;
const MODES = ['date', 'month', 'range', 'time'];
let uid = 0;

const LABELS = {
  fr: {
    prevMonth: 'Mois précédent', nextMonth: 'Mois suivant',
    prevYear: 'Année précédente', nextYear: 'Année suivante',
    prevYears: 'Années précédentes', nextYears: 'Années suivantes',
    chooseMonth: 'Choisir le mois', chooseYear: "Choisir l'année",
    openDate: 'Ouvrir le calendrier', openTime: 'Choisir une heure',
    dialogDate: 'Choisir une date', dialogMonth: 'Choisir un mois',
    dialogRange: 'Choisir une période', dialogTime: 'Choisir une heure',
    today: "Aujourd'hui", thisMonth: 'Ce mois-ci', clear: 'Effacer',
    invalidDate: 'Date invalide (format {f})', invalidMonth: 'Mois invalide (format {f})',
    invalidRange: 'Période invalide (format {f} - {f})', invalidTime: 'Heure invalide (format hh:mm)',
    between: 'Choisissez une valeur entre {min} et {max}', from: 'Choisissez une valeur à partir de {min}',
    until: "Choisissez une valeur jusqu'à {max}", unavailable: "Cette date n'est pas disponible",
    format: { day: 'jj', month: 'mm', year: 'aaaa' },
  },
  en: {
    prevMonth: 'Previous month', nextMonth: 'Next month',
    prevYear: 'Previous year', nextYear: 'Next year',
    prevYears: 'Previous years', nextYears: 'Next years',
    chooseMonth: 'Choose month', chooseYear: 'Choose year',
    openDate: 'Open calendar', openTime: 'Choose a time',
    dialogDate: 'Choose a date', dialogMonth: 'Choose a month',
    dialogRange: 'Choose a period', dialogTime: 'Choose a time',
    today: 'Today', thisMonth: 'This month', clear: 'Clear',
    invalidDate: 'Invalid date (format {f})', invalidMonth: 'Invalid month (format {f})',
    invalidRange: 'Invalid period (format {f} - {f})', invalidTime: 'Invalid time (format hh:mm)',
    between: 'Choose a value between {min} and {max}', from: 'Choose a value from {min}',
    until: 'Choose a value until {max}', unavailable: 'This date is not available',
    format: { day: 'dd', month: 'mm', year: 'yyyy' },
  },
};

const ICONS = {
  calendar: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>',
  clock: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>',
  prev: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>',
  next: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>',
};

// ---------- Dates (heure locale, sans fuseau) ----------

const pad = (n) => String(n).padStart(2, '0');
const toIsoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toIsoMonth = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const today = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); };
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d, n) => {
  const first = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return new Date(first.getFullYear(), first.getMonth(), Math.min(d.getDate(), last));
};
const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const monthEnd = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

function makeDate(y, m, d) {
  if (y < 100) y += 2000;
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d ? date : null;
}

function parseIso(value, mode) {
  if (!value) return null;
  if (mode === 'month') {
    const m = /^(\d{4})-(\d{2})/.exec(value);
    return m ? makeDate(+m[1], +m[2], 1) : null;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? makeDate(+m[1], +m[2], +m[3]) : null;
}

function parseTime(text) {
  const m = /^\s*(\d{1,2})\s*(?:[:hH.]\s*(\d{1,2})?)?\s*$/.exec(text || '') || /^\s*(\d{2})(\d{2})\s*$/.exec(text || '');
  if (!m) return null;
  const h = +m[1], min = m[2] === undefined ? 0 : +m[2];
  return h < 24 && min < 60 ? `${pad(h)}:${pad(min)}` : null;
}

// ---------- Initialisation ----------

export function initDateFields(container = document) {
  const roots = [...container.querySelectorAll('.date-field')];
  if (container.matches?.('.date-field')) roots.unshift(container);
  roots.forEach((root) => {
    if (root.dataset.dateFieldBound) return;
    const inputs = [...root.querySelectorAll('input.date-field__input')];
    if (!inputs.length) return;
    root.dataset.dateFieldBound = 'true';
    setupField(root, inputs);
  });
}

function setupField(root, inputs) {
  const mode = MODES.includes(root.dataset.mode) ? root.dataset.mode : 'date';
  const isRange = mode === 'range' && inputs.length >= 2;
  const effectiveMode = mode === 'range' && !isRange ? 'date' : mode;
  const fields = isRange ? inputs.slice(0, 2) : inputs.slice(0, 1);
  const locale = root.closest('[lang]')?.getAttribute('lang') || 'fr';
  const L = LABELS[locale.slice(0, 2).toLowerCase()] || LABELS.fr;
  const id = ++uid;

  // Ordre jour/mois/année et premier jour de semaine selon la langue
  const order = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
    .formatToParts(new Date(2026, 10, 25)).map((p) => p.type).filter((t) => ['day', 'month', 'year'].includes(t));
  let firstDay = 1;
  try {
    const info = new Intl.Locale(locale).getWeekInfo?.() || new Intl.Locale(locale).weekInfo;
    if (info?.firstDay) firstDay = info.firstDay % 7;
  } catch (e) { /* lundi par défaut */ }

  const fmt = (opts) => new Intl.DateTimeFormat(locale, opts);
  const fmtLong = fmt({ day: 'numeric', month: 'long', year: 'numeric' });
  const fmtMonthYear = fmt({ month: 'long', year: 'numeric' });
  const fmtMonth = fmt({ month: 'long' });
  const fmtShort = fmt({ day: 'numeric', month: 'short', year: 'numeric' });
  const fmtShortNoYear = fmt({ day: 'numeric', month: 'short' });
  const fmtDayLabel = fmt({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const fmtWeekdayShort = fmt({ weekday: 'short' });
  const fmtWeekdayLong = fmt({ weekday: 'long' });
  const pattern = (withDay = true) => order.filter((t) => withDay || t !== 'day').map((t) => L.format[t]).join('/');

  // ---------- DOM ----------
  let wrapper = root.querySelector('.date-field__wrapper');
  if (!wrapper) {
    wrapper = document.createElement('div');
    wrapper.className = 'date-field__wrapper';
    fields[0].before(wrapper);
  }
  const control = document.createElement('input');
  control.type = 'text';
  control.className = 'date-field__control';
  control.id = `${fields[0].id || `date-field-${id}`}-display`;
  control.autocomplete = 'off';
  control.inputMode = effectiveMode === 'time' ? 'numeric' : 'text';
  control.placeholder = effectiveMode === 'time' ? 'hh:mm'
    : effectiveMode === 'month' ? pattern(false)
      : isRange ? `${pattern()} - ${pattern()}` : pattern();
  if (fields[0].placeholder) control.placeholder = fields[0].placeholder;

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'date-field__toggle';
  toggle.innerHTML = effectiveMode === 'time' ? ICONS.clock : ICONS.calendar;
  toggle.setAttribute('aria-label', effectiveMode === 'time' ? L.openTime : L.openDate);
  toggle.setAttribute('aria-haspopup', 'dialog');
  toggle.setAttribute('aria-expanded', 'false');

  const popover = document.createElement('div');
  popover.className = 'date-field__popover';
  popover.id = `date-field-${id}-popover`;
  popover.setAttribute('role', 'dialog');
  popover.setAttribute('aria-label', { date: L.dialogDate, month: L.dialogMonth, range: L.dialogRange, time: L.dialogTime }[isRange ? 'range' : effectiveMode]);
  const supportsPopover = typeof popover.showPopover === 'function';
  if (supportsPopover) popover.setAttribute('popover', 'auto');
  else popover.hidden = true;

  wrapper.append(control, toggle);
  fields.forEach((input) => { input.type = 'hidden'; wrapper.append(input); });
  root.append(popover);
  toggle.setAttribute('aria-controls', popover.id);

  const label = root.querySelector('.date-field__label');
  if (label && (!label.htmlFor || label.htmlFor === fields[0].id)) label.htmlFor = control.id;
  const helper = root.querySelector('.date-field__helper');
  if (helper) {
    helper.id ||= `date-field-${id}-helper`;
    control.setAttribute('aria-describedby', helper.id);
  }

  // ---------- Valeurs ----------
  const readValues = () => fields.map((input) => VALUE.get.call(input));
  const parseValue = (v) => (effectiveMode === 'time' ? parseTime(v) : parseIso(v, effectiveMode === 'month' ? 'month' : 'date'));

  function bounds() {
    const attr = (name) => fields.map((f) => f.getAttribute(name)).find(Boolean) || null;
    if (effectiveMode === 'time') return { min: parseTime(attr('min')), max: parseTime(attr('max')) };
    const kind = effectiveMode === 'month' ? 'month' : 'date';
    let min = parseIso(attr('min'), kind);
    const max = parseIso(attr('max'), kind);
    if (root.hasAttribute('data-disable-past')) {
      const t = effectiveMode === 'month' ? monthStart(today()) : today();
      if (!min || min < t) min = t;
    }
    return { min, max };
  }

  function dayDisabled(d) {
    const { min, max } = bounds();
    if ((min && d < min) || (max && d > max)) return true;
    return typeof root.dsIsDisabled === 'function' ? !!root.dsIsDisabled(new Date(d)) : false;
  }

  function monthDisabled(d) {
    const { min, max } = bounds();
    if (effectiveMode === 'month') return (min && d < monthStart(min)) || (max && d > monthStart(max));
    return (min && monthEnd(d) < min) || (max && monthStart(d) > max);
  }

  function display(values) {
    if (effectiveMode === 'time') return values[0] || '';
    const dates = values.map((v) => parseValue(v));
    if (effectiveMode === 'month') return dates[0] ? fmtMonthYear.format(dates[0]) : '';
    if (!isRange) return dates[0] ? fmtLong.format(dates[0]) : '';
    const [s, e] = dates;
    if (!s && !e) return '';
    if (!e) return `${fmtShort.format(s)} → …`;
    if (!s) return `… → ${fmtShort.format(e)}`;
    if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
      return s.getDate() === e.getDate() ? fmtShort.format(s) : `${s.getDate()} → ${fmtShort.format(e)}`;
    }
    if (s.getFullYear() === e.getFullYear()) return `${fmtShortNoYear.format(s)} → ${fmtShort.format(e)}`;
    return `${fmtShort.format(s)} → ${fmtShort.format(e)}`;
  }

  function numeric(d, withDay = true) {
    const parts = { day: pad(d.getDate()), month: pad(d.getMonth() + 1), year: String(d.getFullYear()) };
    return order.filter((t) => withDay || t !== 'day').map((t) => parts[t]).join('/');
  }

  function editText(values) {
    if (effectiveMode === 'time') return values[0] || '';
    const dates = values.map((v) => parseValue(v));
    if (effectiveMode === 'month') return dates[0] ? numeric(dates[0], false) : '';
    if (!isRange) return dates[0] ? numeric(dates[0]) : '';
    return dates.some(Boolean) ? dates.map((d) => (d ? numeric(d) : '')).join(' - ') : '';
  }

  const monthNames = Array.from({ length: 12 }, (_, i) => [
    fmtMonth.format(new Date(2026, i, 1)).toLowerCase(),
    fmt({ month: 'short' }).format(new Date(2026, i, 1)).toLowerCase().replace('.', ''),
  ]);

  function parseTypedDate(text) {
    const t = text.trim();
    let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
    if (m) return makeDate(+m[1], +m[2], +m[3]);
    if (/^\d{8}$/.test(t)) {
      const widths = { day: 2, month: 2, year: 4 }; let i = 0; const p = {};
      order.forEach((k) => { p[k] = +t.slice(i, i + widths[k]); i += widths[k]; });
      return makeDate(p.year, p.month, p.day);
    }
    const nums = t.split(/[^\d]+/).filter(Boolean).map(Number);
    if (nums.length !== 3) return null;
    const p = {}; order.forEach((k, i) => { p[k] = nums[i]; });
    return makeDate(p.year, p.month, p.day);
  }

  function parseTypedMonth(text) {
    const t = text.trim().toLowerCase();
    let m = /^(\d{4})-(\d{1,2})$/.exec(t);
    if (m) return makeDate(+m[1], +m[2], 1);
    if (/[a-zà-ÿ]/i.test(t)) {
      const year = /(\d{4})/.exec(t);
      const idx = monthNames.findIndex((names) => names.some((n) => n && t.includes(n)));
      return year && idx >= 0 ? makeDate(+year[1], idx + 1, 1) : null;
    }
    const nums = t.split(/[^\d]+/).filter(Boolean).map(Number);
    if (nums.length !== 2) return null;
    const ord = order.filter((k) => k !== 'day');
    const p = {}; ord.forEach((k, i) => { p[k] = nums[i]; });
    return makeDate(p.year, p.month, 1);
  }

  // Écrit les valeurs ISO ; renvoie les inputs modifiés
  let internal = false;
  function writeValues(values) {
    const changed = [];
    internal = true;
    fields.forEach((input, i) => {
      const v = values[i] ?? '';
      if (VALUE.get.call(input) !== v) { VALUE.set.call(input, v); changed.push(input); }
    });
    internal = false;
    return changed;
  }

  function emit(changed) {
    changed.forEach((input) => {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    if (!changed.length) return;
    const values = readValues();
    const detail = isRange
      ? { value: values, start: parseValue(values[0]), end: parseValue(values[1]) }
      : { value: values[0], date: effectiveMode === 'time' ? null : parseValue(values[0]) };
    root.dispatchEvent(new CustomEvent('ds-date:change', { bubbles: true, detail }));
  }

  // ---------- Erreurs ----------
  let ownError = false;
  function setError(message) {
    root.querySelector('.date-field__error[data-date-feedback]')?.remove();
    control.setCustomValidity(message || '');
    if (!message) {
      if (ownError) root.classList.remove('date-field--error');
      ownError = false;
      control.removeAttribute('aria-invalid');
      return;
    }
    const el = document.createElement('span');
    el.className = 'date-field__error';
    el.dataset.dateFeedback = '';
    el.id = `date-field-${id}-error`;
    el.setAttribute('role', 'alert');
    el.textContent = message;
    (helper || wrapper).after(el);
    root.classList.add('date-field--error');
    ownError = true;
    control.setAttribute('aria-invalid', 'true');
    control.setAttribute('aria-describedby', [helper?.id, el.id].filter(Boolean).join(' '));
  }

  function boundsMessage() {
    const { min, max } = bounds();
    const f = (d) => (effectiveMode === 'time' ? d : effectiveMode === 'month' ? fmtMonthYear.format(d) : fmtLong.format(d));
    if (min && max) return L.between.replace('{min}', f(min)).replace('{max}', f(max));
    if (min) return L.from.replace('{min}', f(min));
    if (max) return L.until.replace('{max}', f(max));
    return L.unavailable;
  }

  function checkValue(value) {
    if (!value) return null;
    if (effectiveMode === 'time') {
      const { min, max } = bounds();
      return (min && value < min) || (max && value > max) ? boundsMessage() : null;
    }
    const d = parseValue(value);
    if (effectiveMode === 'month') return monthDisabled(d) ? boundsMessage() : null;
    const { min, max } = bounds();
    if ((min && d < min) || (max && d > max)) return boundsMessage();
    return dayDisabled(d) ? L.unavailable : null;
  }

  // Saisie clavier validée (blur / Entrée)
  function commitText() {
    const text = control.value.trim();
    let values;
    if (!text) values = fields.map(() => '');
    else if (effectiveMode === 'time') values = [parseTime(text)];
    else if (effectiveMode === 'month') { const d = parseTypedMonth(text); values = [d ? toIsoMonth(d) : null]; }
    else if (isRange) {
      const parts = text.split(/\s*(?:→|–|—|\s-\s|\bau\b|\bto\b)\s*/i).filter(Boolean);
      const dates = parts.map(parseTypedDate);
      values = parts.length === 2 && dates.every(Boolean) ? dates.sort((a, b) => a - b).map(toIsoDate) : [null];
    } else { const d = parseTypedDate(text); values = [d ? toIsoDate(d) : null]; }

    if (values.some((v) => v === null)) {
      const msg = effectiveMode === 'time' ? L.invalidTime
        : (effectiveMode === 'month' ? L.invalidMonth : isRange ? L.invalidRange : L.invalidDate)
          .replaceAll('{f}', pattern(effectiveMode !== 'month'));
      setError(msg);
      emit(writeValues(fields.map(() => '')));
      return;
    }
    const problem = values.map(checkValue).find(Boolean);
    setError(problem || '');
    emit(writeValues(problem ? fields.map(() => '') : values));
    if (!problem) control.value = display(readValues());
  }

  // Affichage synchronisé avec les valeurs ISO (sans événement)
  function sync() {
    const values = readValues();
    control.value = document.activeElement === control ? editText(values) : display(values);
    const disabled = fields.some((f) => f.disabled);
    const readOnly = fields.some((f) => f.readOnly || f.hasAttribute('readonly'));
    control.disabled = disabled;
    control.readOnly = readOnly;
    control.required = fields.some((f) => f.required);
    toggle.disabled = disabled || readOnly;
    if (ownError) setError('');
    if (isOpen()) render();
  }

  // input.value = '…' par l'application : met l'affichage à jour (comme un champ natif, sans événement)
  fields.forEach((input) => {
    Object.defineProperty(input, 'value', {
      configurable: true,
      enumerable: true,
      get() { return VALUE.get.call(this); },
      set(v) { VALUE.set.call(this, v); if (!internal) sync(); },
    });
    new MutationObserver(sync).observe(input, { attributes: true, attributeFilter: ['disabled', 'readonly', 'required', 'min', 'max'] });
  });
  fields[0].form?.addEventListener('reset', () => setTimeout(sync));

  // ---------- Popover ----------
  let view = effectiveMode === 'month' ? 'months' : 'days';
  let cursor = today();
  let anchor = null; // début de plage en cours de sélection
  let preview = null; // jour survolé pendant la sélection de la fin

  const isOpen = () => (supportsPopover ? popover.matches(':popover-open') : !popover.hidden);

  function initialCursor() {
    const dates = readValues().map((v) => parseValue(v)).filter((d) => d instanceof Date);
    const { min, max } = bounds();
    let c = dates[0] || today();
    if (min && c < min) c = min;
    if (max && c > max) c = max;
    return effectiveMode === 'month' ? monthStart(c) : c;
  }

  function open() {
    if (control.disabled || control.readOnly || isOpen()) return;
    if (supportsPopover) popover.showPopover();
    else { prepare(); popover.hidden = false; afterOpen(); }
  }

  function close(focusControl = false) {
    if (!isOpen()) return;
    if (supportsPopover) popover.hidePopover();
    else { popover.hidden = true; afterClose(); }
    if (focusControl) control.focus();
  }

  function prepare() {
    view = effectiveMode === 'month' ? 'months' : 'days';
    cursor = initialCursor();
    anchor = null;
    preview = null;
    render();
  }

  function position() {
    const r = wrapper.getBoundingClientRect();
    const p = popover.getBoundingClientRect();
    const gap = 4, margin = 8;
    let top = r.bottom + gap;
    if (top + p.height > innerHeight - margin && r.top - p.height - gap > margin) top = r.top - p.height - gap;
    let left = r.left;
    left = Math.max(margin, Math.min(left, innerWidth - p.width - margin));
    popover.style.top = `${Math.round(top)}px`;
    popover.style.left = `${Math.round(left)}px`;
  }

  const onViewport = () => position();
  function afterOpen() {
    toggle.setAttribute('aria-expanded', 'true');
    position();
    addEventListener('scroll', onViewport, true);
    addEventListener('resize', onViewport);
    focusActive();
  }
  function afterClose() {
    toggle.setAttribute('aria-expanded', 'false');
    removeEventListener('scroll', onViewport, true);
    removeEventListener('resize', onViewport);
  }

  if (supportsPopover) {
    toggle.popoverTargetElement = popover;
    toggle.popoverTargetAction = 'toggle';
    popover.addEventListener('beforetoggle', (e) => { if (e.newState === 'open') prepare(); });
    popover.addEventListener('toggle', (e) => { if (e.newState === 'open') afterOpen(); else afterClose(); });
  } else {
    toggle.addEventListener('click', () => (isOpen() ? close() : open()));
    document.addEventListener('click', (e) => { if (isOpen() && !root.contains(e.target)) close(); });
  }

  // ---------- Rendu ----------
  function navButton(dir, labelText) {
    return `<button type="button" class="date-field__nav" data-nav="${dir}" aria-label="${labelText}">${dir < 0 ? ICONS.prev : ICONS.next}</button>`;
  }

  function render() {
    if (effectiveMode === 'time') return renderTime();
    const values = readValues().map((v) => parseValue(v));
    const { min, max } = bounds();
    let html = '';
    if (view === 'days') {
      const title = fmtMonthYear.format(cursor);
      const prevOff = min && monthEnd(addMonths(cursor, -1)) < min;
      const nextOff = max && monthStart(addMonths(cursor, 1)) > max;
      html += `<div class="date-field__header">${navButton(-1, L.prevMonth)}<button type="button" class="date-field__title" data-view="months" aria-label="${L.chooseMonth} (${title})" aria-live="polite">${title}</button>${navButton(1, L.nextMonth)}</div>`;
      html += `<table class="date-field__grid" role="grid" aria-label="${title}"><thead><tr>`;
      for (let i = 0; i < 7; i++) {
        const wd = new Date(2026, 10, 1 + ((firstDay + i) % 7)); // 1er nov. 2026 = dimanche (getDay 0)
        html += `<th scope="col" abbr="${fmtWeekdayLong.format(wd)}">${fmtWeekdayShort.format(wd).replace('.', '')}</th>`;
      }
      html += '</tr></thead><tbody>';
      const start = addDays(monthStart(cursor), -((monthStart(cursor).getDay() - firstDay + 7) % 7));
      const t = today();
      let [rs, re] = isRange ? values : [values[0], values[0]];
      if (isRange && anchor) {
        const other = preview || anchor;
        [rs, re] = anchor <= other ? [anchor, other] : [other, anchor];
      }
      for (let w = 0; w < 6; w++) {
        html += '<tr>';
        for (let i = 0; i < 7; i++) {
          const d = addDays(start, w * 7 + i);
          const iso = toIsoDate(d);
          const classes = ['date-field__day'];
          if (d.getMonth() !== cursor.getMonth()) classes.push('date-field__day--outside');
          if (iso === toIsoDate(t)) classes.push('date-field__day--today');
          let selected = false;
          if (isRange) {
            if (rs && iso === toIsoDate(rs)) { classes.push('date-field__day--range-start'); selected = true; }
            if (re && iso === toIsoDate(re)) { classes.push('date-field__day--range-end'); selected = true; }
            if (rs && re && d > rs && d < re) classes.push('date-field__day--in-range');
          } else if (rs && iso === toIsoDate(rs)) { classes.push('date-field__day--selected'); selected = true; }
          const disabled = dayDisabled(d);
          const focus = iso === toIsoDate(cursor);
          html += `<td${selected ? ' aria-selected="true"' : ''}><button type="button" class="${classes.join(' ')}" data-date="${iso}" tabindex="${focus ? 0 : -1}" aria-label="${fmtDayLabel.format(d)}"${iso === toIsoDate(t) ? ' aria-current="date"' : ''}${disabled ? ' aria-disabled="true"' : ''}>${d.getDate()}</button></td>`;
        }
        html += '</tr>';
      }
      html += '</tbody></table>';
      queueMicrotask(() => {
        popover.querySelector('[data-nav="-1"]').disabled = !!prevOff;
        popover.querySelector('[data-nav="1"]').disabled = !!nextOff;
      });
    } else if (view === 'months') {
      const year = cursor.getFullYear();
      const selected = values[0];
      html += `<div class="date-field__header">${navButton(-1, L.prevYear)}<button type="button" class="date-field__title" data-view="years" aria-label="${L.chooseYear} (${year})" aria-live="polite">${year}</button>${navButton(1, L.nextYear)}</div><div class="date-field__cells" role="group" aria-label="${year}">`;
      const now = today();
      for (let m = 0; m < 12; m++) {
        const d = new Date(year, m, 1);
        const isSel = effectiveMode === 'month' && selected && toIsoMonth(selected) === toIsoMonth(d);
        const cls = ['date-field__cell'];
        if (toIsoMonth(d) === toIsoMonth(now)) cls.push('date-field__cell--current');
        if (isSel) cls.push('date-field__cell--selected');
        html += `<button type="button" class="${cls.join(' ')}" data-month="${toIsoMonth(d)}" tabindex="${m === cursor.getMonth() ? 0 : -1}" aria-label="${fmtMonthYear.format(d)}"${isSel ? ' aria-pressed="true"' : ''}${monthDisabled(d) ? ' aria-disabled="true"' : ''}>${fmtMonth.format(d)}</button>`;
      }
      html += '</div>';
    } else {
      const first = Math.floor(cursor.getFullYear() / 12) * 12;
      html += `<div class="date-field__header">${navButton(-1, L.prevYears)}<span class="date-field__title" aria-live="polite">${first} – ${first + 11}</span>${navButton(1, L.nextYears)}</div><div class="date-field__cells" role="group" aria-label="${first} – ${first + 11}">`;
      for (let y = first; y < first + 12; y++) {
        const off = (min && y < min.getFullYear()) || (max && y > max.getFullYear());
        const cls = ['date-field__cell'];
        if (y === today().getFullYear()) cls.push('date-field__cell--current');
        html += `<button type="button" class="${cls.join(' ')}" data-year="${y}" tabindex="${y === cursor.getFullYear() ? 0 : -1}"${off ? ' aria-disabled="true"' : ''}>${y}</button>`;
      }
      html += '</div>';
    }
    html += footer();
    popover.innerHTML = html;
  }

  function footer() {
    const parts = [];
    const showToday = root.dataset.today !== 'false';
    if (showToday) {
      const t = effectiveMode === 'month' ? monthStart(today()) : today();
      const off = effectiveMode === 'month' ? monthDisabled(t) : dayDisabled(t);
      if (!off) parts.push(`<button type="button" class="btn btn-sm btn-link" data-action="today">${effectiveMode === 'month' ? L.thisMonth : L.today}</button>`);
    }
    if (!fields.some((f) => f.required)) parts.push(`<button type="button" class="btn btn-sm btn-link" data-action="clear">${L.clear}</button>`);
    return `<div class="date-field__footer">${parts.join('')}</div>`;
  }

  function renderTime() {
    const step = Math.max(1, parseInt(root.dataset.step, 10) || 15);
    const { min, max } = bounds();
    const current = readValues()[0];
    let html = `<ul class="date-field__slots" role="listbox" aria-label="${L.dialogTime}">`;
    for (let m = 0; m < 24 * 60; m += step) {
      const v = `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
      if ((min && v < min) || (max && v > max)) continue;
      html += `<li role="option" class="date-field__slot" data-time="${v}" tabindex="-1" aria-selected="${v === current}">${v}</li>`;
    }
    html += `</ul>${fields.some((f) => f.required) ? '' : `<div class="date-field__footer"><button type="button" class="btn btn-sm btn-link" data-action="clear">${L.clear}</button></div>`}`;
    popover.innerHTML = html;
  }

  function focusActive() {
    if (effectiveMode === 'time') {
      const slots = [...popover.querySelectorAll('.date-field__slot')];
      const current = readValues()[0];
      const now = `${pad(new Date().getHours())}:${pad(new Date().getMinutes())}`;
      const target = slots.find((s) => s.dataset.time === current) || slots.find((s) => s.dataset.time >= now) || slots[0];
      if (!target) return;
      target.tabIndex = 0;
      target.scrollIntoView({ block: 'center' });
      target.focus({ preventScroll: true });
      return;
    }
    popover.querySelector('.date-field__day[tabindex="0"], .date-field__cell[tabindex="0"]')?.focus();
  }

  function moveCursor(next) {
    cursor = next;
    if (isRange && anchor && view === 'days') preview = next;
    render();
    focusActive();
  }

  // ---------- Sélection ----------
  function choose(values) {
    setError('');
    emit(writeValues(values));
    control.value = display(readValues());
    close(true);
  }

  function pickDay(d) {
    if (dayDisabled(d)) return;
    if (!isRange) return choose([toIsoDate(d)]);
    if (!anchor) {
      anchor = d; preview = d; cursor = d;
      render(); focusActive();
      return;
    }
    const [s, e] = anchor <= d ? [anchor, d] : [d, anchor];
    anchor = null; preview = null;
    choose([toIsoDate(s), toIsoDate(e)]);
  }

  popover.addEventListener('click', (e) => {
    const t = e.target.closest('button, [data-time]');
    if (!t || !popover.contains(t)) return;
    if (t.dataset.nav) {
      const dir = +t.dataset.nav;
      cursor = view === 'days' ? addMonths(cursor, dir) : view === 'months' ? addMonths(cursor, dir * 12) : addMonths(cursor, dir * 144);
      render();
      popover.querySelector(`[data-nav="${dir}"]`)?.focus();
    } else if (t.dataset.view) {
      view = t.dataset.view; render(); focusActive();
    } else if (t.dataset.date) {
      if (t.getAttribute('aria-disabled') !== 'true') pickDay(parseIso(t.dataset.date, 'date'));
    } else if (t.dataset.month) {
      if (t.getAttribute('aria-disabled') === 'true') return;
      const d = parseIso(t.dataset.month, 'month');
      if (effectiveMode === 'month') choose([toIsoMonth(d)]);
      else { cursor = new Date(d.getFullYear(), d.getMonth(), Math.min(cursor.getDate(), monthEnd(d).getDate())); view = 'days'; render(); focusActive(); }
    } else if (t.dataset.year) {
      if (t.getAttribute('aria-disabled') === 'true') return;
      cursor = new Date(+t.dataset.year, cursor.getMonth(), 1); view = 'months'; render(); focusActive();
    } else if (t.dataset.time) {
      choose([t.dataset.time]);
    } else if (t.dataset.action === 'today') {
      const d = today();
      if (effectiveMode === 'month') choose([toIsoMonth(d)]);
      else if (isRange) { view = 'days'; moveCursor(d); }
      else choose([toIsoDate(d)]);
    } else if (t.dataset.action === 'clear') {
      choose(fields.map(() => ''));
    }
  });

  // Aperçu de la plage au survol : classes mises à jour sur place (re-rendre la grille
  // remplacerait le bouton entre mousedown et mouseup et annulerait le clic)
  function updateRangePreview() {
    const other = preview || anchor;
    const [s, e] = anchor <= other ? [anchor, other] : [other, anchor];
    const si = toIsoDate(s), ei = toIsoDate(e);
    popover.querySelectorAll('.date-field__day').forEach((b) => {
      const iso = b.dataset.date;
      b.classList.toggle('date-field__day--range-start', iso === si);
      b.classList.toggle('date-field__day--range-end', iso === ei);
      b.classList.toggle('date-field__day--in-range', iso > si && iso < ei);
      const td = b.parentElement;
      if (iso === si || iso === ei) td.setAttribute('aria-selected', 'true');
      else td.removeAttribute('aria-selected');
    });
  }

  popover.addEventListener('mouseover', (e) => {
    const day = e.target.closest('.date-field__day');
    if (!isRange || !anchor || !day) return;
    const d = parseIso(day.dataset.date, 'date');
    if (preview && toIsoDate(preview) === toIsoDate(d)) return;
    preview = d;
    updateRangePreview();
  });

  popover.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(true); return; }
    if (e.key === 'Tab') return;
    const target = e.target;
    if (effectiveMode === 'time' && target.dataset.time) {
      const slots = [...popover.querySelectorAll('.date-field__slot')];
      const i = slots.indexOf(target);
      const go = (j) => { const s = slots[Math.max(0, Math.min(slots.length - 1, j))]; target.tabIndex = -1; s.tabIndex = 0; s.focus(); };
      const keys = { ArrowDown: () => go(i + 1), ArrowUp: () => go(i - 1), Home: () => go(0), End: () => go(slots.length - 1), PageDown: () => go(i + 4), PageUp: () => go(i - 4), Enter: () => choose([target.dataset.time]), ' ': () => choose([target.dataset.time]) };
      if (keys[e.key]) { e.preventDefault(); keys[e.key](); }
      return;
    }
    const inGrid = target.dataset.date || target.dataset.month || target.dataset.year;
    if (!inGrid) return;
    let next = null;
    if (view === 'days') {
      const dow = (cursor.getDay() - firstDay + 7) % 7;
      next = {
        ArrowLeft: () => addDays(cursor, -1), ArrowRight: () => addDays(cursor, 1),
        ArrowUp: () => addDays(cursor, -7), ArrowDown: () => addDays(cursor, 7),
        Home: () => addDays(cursor, -dow), End: () => addDays(cursor, 6 - dow),
        PageUp: () => addMonths(cursor, e.shiftKey ? -12 : -1), PageDown: () => addMonths(cursor, e.shiftKey ? 12 : 1),
      }[e.key];
    } else {
      const unit = view === 'months' ? 1 : 12;
      next = {
        ArrowLeft: () => addMonths(cursor, -unit), ArrowRight: () => addMonths(cursor, unit),
        ArrowUp: () => addMonths(cursor, -3 * unit), ArrowDown: () => addMonths(cursor, 3 * unit),
        PageUp: () => addMonths(cursor, -12 * unit), PageDown: () => addMonths(cursor, 12 * unit),
      }[e.key];
    }
    if (next) { e.preventDefault(); moveCursor(next()); return; }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); target.click(); }
  });

  // ---------- Champ de saisie ----------
  // Au focus : format numérique éditable (sauf saisie invalide en cours de correction)
  control.addEventListener('focus', () => { if (!ownError) control.value = editText(readValues()); });
  control.addEventListener('blur', () => {
    if (control.value.trim() !== editText(readValues())) commitText();
    else control.value = display(readValues());
  });
  control.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || (e.altKey && e.key === 'ArrowDown')) { e.preventDefault(); open(); }
    else if (e.key === 'Enter' && control.value.trim() !== editText(readValues())) {
      commitText();
      if (!ownError) control.value = editText(readValues());
    }
  });

  // ---------- API ----------
  root.dsSetValue = (value, { emit: notify = false } = {}) => {
    const values = Array.isArray(value) ? value : [value];
    const changed = writeValues(fields.map((_, i) => values[i] ?? ''));
    sync();
    if (notify) emit(changed);
  };
  root.dsClear = (opts) => root.dsSetValue(fields.map(() => ''), opts);
  root.dsOpen = open;
  root.dsClose = () => close(false);
  root.dsUpdate = sync;

  sync();
}
